import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import {
  FLOAT_HANDLE_HIT_PX,
  floatRotationHandle,
  floatWorldCorners,
  floatWorldEdgeMidpoints,
  imageDataToCanvas,
  resolveFloatScaleAxes,
} from '../../lib/imageEditorFloat';
import {
  applyStyleToTextRange,
  flattenRunsToText,
  resolveEditorTextContent,
  summarizeStyleInTextRange,
} from '../../lib/imageEditorText';
import {
  applyPlainTextPasteToEditSurface,
  clipboardPlainTextFromPasteEvent,
  focusEditSurfaceCaretEnd,
  getEditSurfaceSelectionOffsets,
  plainTextFromEditSurface,
  runsFromEditSurface,
  setEditSurfaceFromRuns,
  setEditSurfaceSelectionOffsets,
  textEditKeyAction,
} from '../../lib/imageEditorTextEdit';

/** Inset from float edge used as body-drag chrome while editing text. */
const EDIT_MOVE_FRAME_PX = 8;
/** Mini-toolbar height + gap above rotate handle. */
const RANGE_TOOLBAR_GAP_PX = 8;
const RANGE_TOOLBAR_HEIGHT_PX = 34;

/**
 * Viewport overlay for the single uncommitted float (preview + handles).
 * Text edit: contentEditable for caret/selection; handles always visible.
 * Slice B: range mini-toolbar (Bold / Italic / color) when selection is non-collapsed.
 * Edit surface fills the float metrics box; move-frame strips overlay edges
 * without shrinking the text box (avoids last-glyph clip / caret soft-wrap).
 *
 * @param {{
 *   float: import('../../lib/imageEditorFloat').EditorFloat,
 *   zoom: number,
 *   busy?: boolean,
 *   textEditMode?: boolean,
 *   onPointerDownHandle: (hit: string, event: React.PointerEvent) => void,
 *   onPointerMoveHandle: (event: React.PointerEvent) => void,
 *   onPointerUpHandle: (event: React.PointerEvent) => void,
 *   onEnterTextEdit?: () => void,
 *   onTextEditInput?: (text: string) => void,
 *   onTextEditRuns?: (runs: import('../../lib/imageEditorText').EditorTextRun[]) => void,
 *   onTextEditCommit?: () => void,
 *   onTextEditExit?: () => void,
 *   onTextEditFlushReady?: (flush: (() => boolean)|null) => void,
 * }} props
 */
export default function ImageEditorFloatOverlay({
  float,
  zoom,
  busy = false,
  textEditMode = false,
  onPointerDownHandle,
  onPointerMoveHandle,
  onPointerUpHandle,
  onEnterTextEdit,
  onTextEditInput,
  onTextEditRuns,
  onTextEditCommit,
  onTextEditExit,
  onTextEditFlushReady,
}) {
  const previewRef = useRef(null);
  const editRef = useRef(null);
  const seededEditRef = useRef(false);
  const syncingDomRef = useRef(false);
  const composingRef = useRef(false);
  const [rangeSel, setRangeSel] = useState(
    /** @type {{ start: number, end: number }|null} */ (null),
  );

  const editingText =
    Boolean(textEditMode) && float?.origin === 'text' && Boolean(float.textParams);

  useEffect(() => {
    if (!float?.imageData || !previewRef.current) return;
    if (editingText) return;
    const src = imageDataToCanvas(float.imageData);
    if (!src) return;
    const dest = previewRef.current;
    dest.width = float.width;
    dest.height = float.height;
    const ctx = dest.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, dest.width, dest.height);
    ctx.drawImage(src, 0, 0);
  }, [float, editingText]);

  useEffect(() => {
    if (!editingText) {
      seededEditRef.current = false;
      setRangeSel(null);
      return undefined;
    }
    let cancelled = false;
    const trySeed = () => {
      if (cancelled || seededEditRef.current) return;
      const el = editRef.current;
      if (!el) {
        window.requestAnimationFrame(trySeed);
        return;
      }
      const tp = float.textParams;
      const resolved = resolveEditorTextContent(tp || {});
      syncingDomRef.current = true;
      setEditSurfaceFromRuns(el, resolved.runs, resolved.params);
      syncingDomRef.current = false;
      seededEditRef.current = true;
      focusEditSurfaceCaretEnd(el);
    };
    const id = window.requestAnimationFrame(trySeed);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed only on enter edit
  }, [editingText]);

  // Track native selection inside the edit surface for the mini-toolbar.
  useEffect(() => {
    if (!editingText) return undefined;
    const refresh = () => {
      if (composingRef.current) return;
      const el = editRef.current;
      if (!el) {
        setRangeSel(null);
        return;
      }
      const offsets = getEditSurfaceSelectionOffsets(el);
      if (!offsets || offsets.collapsed) {
        setRangeSel(null);
        return;
      }
      setRangeSel({ start: offsets.start, end: offsets.end });
    };
    document.addEventListener('selectionchange', refresh);
    return () => document.removeEventListener('selectionchange', refresh);
  }, [editingText]);

  const pushRunsFromSurface = () => {
    const el = editRef.current;
    if (!el || syncingDomRef.current) return false;
    if (typeof onTextEditRuns === 'function') {
      const nextRuns = runsFromEditSurface(el, float?.textParams || {});
      onTextEditRuns(nextRuns);
      return true;
    }
    if (typeof onTextEditInput === 'function') {
      onTextEditInput(plainTextFromEditSurface(el));
      return true;
    }
    return false;
  };

  // Register flush so Modal can sync DOM→runs on every exit/commit path.
  useEffect(() => {
    if (!editingText || typeof onTextEditFlushReady !== 'function') {
      if (typeof onTextEditFlushReady === 'function') onTextEditFlushReady(null);
      return undefined;
    }
    const flush = () => {
      // Force sync even mid-IME — exit/commit must capture final DOM.
      composingRef.current = false;
      return pushRunsFromSurface();
    };
    onTextEditFlushReady(flush);
    return () => onTextEditFlushReady(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable registration while editing
  }, [editingText, onTextEditFlushReady, float?.textParams]);

  if (!float) return null;

  const { scaleX, scaleY } = resolveFloatScaleAxes(float);
  const dispW = Math.max(
    float.width * scaleX * zoom,
    editingText
      ? (Number(float.textParams?.fontSize) || 32) * scaleX * zoom * 0.75 +
          8 * scaleX * zoom
      : 0,
  );
  const dispH = Math.max(
    float.height * scaleY * zoom,
    editingText
      ? (Number(float.textParams?.fontSize) || 32) * scaleY * zoom * 1.25 +
          8 * scaleY * zoom
      : 0,
  );
  const left = float.cx * zoom - dispW / 2;
  const top = float.cy * zoom - dispH / 2;
  const corners = floatWorldCorners(float);
  const edgeMids = floatWorldEdgeMidpoints(float);
  const rot = floatRotationHandle(float);
  const handleSize = FLOAT_HANDLE_HIT_PX;
  const tp = float.textParams;
  const fontSizeCss = Math.max(
    1,
    (Number(tp?.fontSize) || 32) * scaleY * zoom,
  );
  const padCss = 4 * ((scaleX + scaleY) / 2) * zoom;
  const frame = EDIT_MOVE_FRAME_PX;

  const handlePointerBind = {
    onPointerMove: onPointerMoveHandle,
    onPointerUp: onPointerUpHandle,
    onPointerCancel: onPointerUpHandle,
  };

  const resolvedTp = tp ? resolveEditorTextContent(tp) : null;
  const rangeSummary =
    rangeSel && resolvedTp
      ? summarizeStyleInTextRange(
          resolvedTp.runs,
          rangeSel.start,
          rangeSel.end,
          resolvedTp.params,
        )
      : null;

  const applyRangeStyle = (stylePatch) => {
    if (!rangeSel || !resolvedTp || busy) return;
    const el = editRef.current;
    const nextRuns = applyStyleToTextRange(
      resolvedTp.runs,
      rangeSel.start,
      rangeSel.end,
      stylePatch,
      resolvedTp.params,
    );
    if (typeof onTextEditRuns === 'function') {
      onTextEditRuns(nextRuns);
    } else if (typeof onTextEditInput === 'function') {
      onTextEditInput(flattenRunsToText(nextRuns));
    }
    // Refresh styled DOM + keep the same selection for further toggles.
    if (el) {
      syncingDomRef.current = true;
      setEditSurfaceFromRuns(el, nextRuns, {
        ...resolvedTp.params,
        runs: nextRuns,
      });
      syncingDomRef.current = false;
      el.focus();
      setEditSurfaceSelectionOffsets(el, rangeSel.start, rangeSel.end);
      setRangeSel({ start: rangeSel.start, end: rangeSel.end });
    }
  };

  // Anchor mini-toolbar above the rotate handle so it never covers corner/rotate hits.
  const toolbarTop =
    Math.min(top, rot.y * zoom - handleSize / 2) -
    RANGE_TOOLBAR_HEIGHT_PX -
    RANGE_TOOLBAR_GAP_PX;
  const toolbarLeft = left;

  return (
    <Box
      aria-label="Uncommitted float transform"
      data-testid="image-editor-float-overlay"
      data-text-float-editing={editingText ? 'true' : 'false'}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 4,
      }}
    >
      {editingText && rangeSel && rangeSummary ? (
        <Box
          data-float-chrome="true"
          data-no-float-commit="true"
          data-text-range-toolbar="true"
          data-testid="text-range-format-toolbar"
          onMouseDown={(e) => {
            // Keep contentEditable selection while clicking toolbar controls.
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{
            position: 'absolute',
            left: toolbarLeft,
            top: Math.max(2, toolbarTop),
            height: RANGE_TOOLBAR_HEIGHT_PX,
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 2,
            borderRadius: 1,
            pointerEvents: 'auto',
            zIndex: 9,
          }}
        >
          <Tooltip title="Bold selection">
            <IconButton
              size="small"
              aria-label="Bold selection"
              data-testid="text-range-bold"
              color={rangeSummary.bold ? 'primary' : 'default'}
              disabled={busy}
              onClick={() =>
                applyRangeStyle({ bold: rangeSummary.bold !== true })
              }
              sx={{ p: 0.5 }}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic selection">
            <IconButton
              size="small"
              aria-label="Italic selection"
              data-testid="text-range-italic"
              color={rangeSummary.italic ? 'primary' : 'default'}
              disabled={busy}
              onClick={() =>
                applyRangeStyle({ italic: rangeSummary.italic !== true })
              }
              sx={{ p: 0.5 }}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box
            component="input"
            type="color"
            aria-label="Color selection"
            data-testid="text-range-color"
            disabled={busy}
            value={
              rangeSummary.color && /^#([0-9a-fA-F]{6})$/.test(rangeSummary.color)
                ? rangeSummary.color
                : tp?.color || '#000000'
            }
            onChange={(e) => {
              applyRangeStyle({ color: e.target.value });
            }}
            sx={{
              width: 28,
              height: 28,
              p: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              bgcolor: 'transparent',
              cursor: 'pointer',
            }}
          />
        </Box>
      ) : null}

      {editingText ? (
        <Box
          data-float-chrome="true"
          data-testid="text-float-edit-shell"
          data-text-float-editing="true"
          sx={{
            position: 'absolute',
            left,
            top,
            width: dispW,
            height: dispH,
            transform: `rotate(${float.rotationDeg}deg)`,
            transformOrigin: 'center center',
            outline: '1px solid rgba(0,229,255,0.9)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
            boxSizing: 'border-box',
            pointerEvents: busy ? 'none' : 'auto',
            cursor: 'move',
            zIndex: 6,
          }}
          onPointerDown={(e) => {
            // Outer chrome / frame: body-move. Interior editable stops this.
            onPointerDownHandle('move', e);
          }}
          {...handlePointerBind}
        >
          {/* Edge move strips overlay the editable edges (do not inset/shrink
              the text box — that caused last-character clip + caret soft-wrap). */}
          {[
            {
              id: 'n',
              sx: {
                top: 0,
                left: handleSize,
                right: handleSize,
                height: frame,
              },
            },
            {
              id: 's',
              sx: {
                bottom: 0,
                left: handleSize,
                right: handleSize,
                height: frame,
              },
            },
            {
              id: 'w',
              sx: {
                left: 0,
                top: handleSize,
                bottom: handleSize,
                width: frame,
              },
            },
            {
              id: 'e',
              sx: {
                right: 0,
                top: handleSize,
                bottom: handleSize,
                width: frame,
              },
            },
          ].map((edge) => (
            <Box
              key={edge.id}
              data-float-chrome="true"
              data-testid={`text-float-move-${edge.id}`}
              onPointerDown={(e) => {
                e.stopPropagation();
                onPointerDownHandle('move', e);
              }}
              {...handlePointerBind}
              sx={{
                position: 'absolute',
                ...edge.sx,
                cursor: 'move',
                zIndex: 7,
                backgroundColor: 'transparent',
              }}
            />
          ))}
          {/* Text surface fills the float metrics box (same as layout raster). */}
          <Box
            ref={editRef}
            component="div"
            contentEditable={!busy}
            suppressContentEditableWarning
            role="textbox"
            aria-label="Edit text on canvas"
            data-float-chrome="true"
            data-text-edit-surface="true"
            data-no-float-commit="true"
            data-testid="text-float-edit-surface"
            spellCheck
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              boxSizing: 'border-box',
              // Match layout TEXT_PAD scaled into viewport space.
              padding: `${padCss}px`,
              margin: 0,
              overflow: 'hidden',
              backgroundColor: 'rgba(0,0,0,0.04)',
              color: tp?.color || '#000000',
              caretColor: tp?.color || '#000000',
              fontFamily: tp?.fontFamily || 'Arial, Helvetica, sans-serif',
              fontSize: `${fontSizeCss}px`,
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: 1.25,
              textAlign: tp?.align || 'left',
              whiteSpace: 'pre-wrap',
              // Avoid mid-glyph soft-wrap from a slightly-tight box; layout
              // already sizes width to the line (+ caret slack).
              wordBreak: 'normal',
              overflowWrap: 'normal',
              cursor: 'text',
              transform: 'none',
              zIndex: 6,
              '& [data-text-run="true"]': {
                whiteSpace: 'pre-wrap',
              },
            }}
            onPointerDown={(e) => {
              // Caret / range selection — do not start body-move.
              e.stopPropagation();
            }}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionUpdate={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              composingRef.current = false;
              // Sync once after IME commits the composed characters.
              pushRunsFromSurface();
            }}
            onPaste={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (busy || composingRef.current) return;
              const el = editRef.current;
              if (!el) return;
              const plain = clipboardPlainTextFromPasteEvent(e);
              const resolved = resolveEditorTextContent(tp || {});
              const { runs: nextRuns, caret } = applyPlainTextPasteToEditSurface(
                el,
                plain,
                resolved.runs,
                resolved.params,
              );
              if (typeof onTextEditRuns === 'function') {
                onTextEditRuns(nextRuns);
              } else if (typeof onTextEditInput === 'function') {
                onTextEditInput(flattenRunsToText(nextRuns));
              }
              syncingDomRef.current = true;
              setEditSurfaceFromRuns(el, nextRuns, {
                ...resolved.params,
                runs: nextRuns,
              });
              syncingDomRef.current = false;
              el.focus();
              setEditSurfaceSelectionOffsets(el, caret, caret);
              setRangeSel(null);
            }}
            onInput={() => {
              if (syncingDomRef.current) return;
              // Slice C: do not sync mid-IME composition (avoids run splintering).
              if (composingRef.current) return;
              pushRunsFromSurface();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                composingRef.current = false;
                pushRunsFromSurface();
                if (typeof onTextEditExit === 'function') onTextEditExit();
                return;
              }
              const action = textEditKeyAction(e);
              if (action === 'commit') {
                e.preventDefault();
                e.stopPropagation();
                composingRef.current = false;
                pushRunsFromSurface();
                if (typeof onTextEditCommit === 'function') onTextEditCommit();
                return;
              }
            }}
          />
        </Box>
      ) : (
        <Box
          component="canvas"
          ref={previewRef}
          data-float-chrome="true"
          sx={{
            position: 'absolute',
            left,
            top,
            width: dispW,
            height: dispH,
            transform: `rotate(${float.rotationDeg}deg)`,
            transformOrigin: 'center center',
            imageRendering: 'pixelated',
            pointerEvents: busy ? 'none' : 'auto',
            cursor: 'move',
            outline: '1px solid rgba(0,229,255,0.9)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
          }}
          onPointerDown={(e) => onPointerDownHandle('move', e)}
          {...handlePointerBind}
          onDoubleClick={(e) => {
            if (float.origin !== 'text' || busy) return;
            e.preventDefault();
            e.stopPropagation();
            if (typeof onEnterTextEdit === 'function') onEnterTextEdit();
          }}
        />
      )}

      {/* Handles always on — tight targets above edit surface / preview. */}
      {corners.map((c) => (
        <Box
          key={c.id}
          data-float-chrome="true"
          data-testid={`float-handle-${c.id}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            onPointerDownHandle(c.id, e);
          }}
          {...handlePointerBind}
          sx={{
            position: 'absolute',
            left: c.x * zoom - handleSize / 2,
            top: c.y * zoom - handleSize / 2,
            width: handleSize,
            height: handleSize,
            bgcolor: '#00e5ff',
            border: '1px solid #003344',
            pointerEvents: busy ? 'none' : 'auto',
            cursor:
              c.id === 'nw' || c.id === 'se' ? 'nwse-resize' : 'nesw-resize',
            zIndex: 8,
          }}
        />
      ))}
      {edgeMids.map((e) => (
        <Box
          key={e.id}
          data-float-chrome="true"
          data-testid={`float-handle-${e.id}`}
          onPointerDown={(ev) => {
            ev.stopPropagation();
            onPointerDownHandle(e.id, ev);
          }}
          {...handlePointerBind}
          sx={{
            position: 'absolute',
            left: e.x * zoom - handleSize / 2,
            top: e.y * zoom - handleSize / 2,
            width: handleSize,
            height: handleSize,
            bgcolor: '#00e5ff',
            border: '1px solid #003344',
            pointerEvents: busy ? 'none' : 'auto',
            cursor: e.id === 'n' || e.id === 's' ? 'ns-resize' : 'ew-resize',
            zIndex: 8,
          }}
        />
      ))}
      <Box
        data-float-chrome="true"
        data-testid="float-handle-rotate"
        onPointerDown={(e) => {
          e.stopPropagation();
          onPointerDownHandle('rotate', e);
        }}
        {...handlePointerBind}
        title="Rotate"
        sx={{
          position: 'absolute',
          left: rot.x * zoom - handleSize / 2,
          top: rot.y * zoom - handleSize / 2,
          width: handleSize,
          height: handleSize,
          borderRadius: '50%',
          bgcolor: '#ffb74d',
          border: '1px solid #5d4037',
          pointerEvents: busy ? 'none' : 'auto',
          cursor: 'grab',
          zIndex: 8,
        }}
      />
    </Box>
  );
}
